import type { Message } from 'shared/domain/messages/message';
import { logInfo } from 'setup/logger/logInfo';
import { getErrorToastHideDelay, isNetworkFailureError } from 'shared/utils/errorHandling';
import {
  showErrorToastAndLog,
  showErrorToastWithMessage,
  showGenericErrorToast,
} from 'services/toaster';

/** Uploads failing for a lost network within this time are one failure to the user and the logs. */
export const NETWORK_FAILURE_NOTICE_INTERVAL_MS = 60_000;

let lastNetworkFailureNoticeAt = -Infinity;

/**
 * A lost network fails every upload of a batch at once. It is expected
 * (offline, a laptop asleep) and not a defect: one toast and one info log per
 * series, not an error for each file. Resolves whether it was such a failure.
 */
function noticeNetworkFailure(message: Message, genericLogMessage: string): boolean {
  if (!isNetworkFailureError(message.error)) {
    return false;
  }
  const now = Date.now();
  if (now - lastNetworkFailureNoticeAt >= NETWORK_FAILURE_NOTICE_INTERVAL_MS) {
    lastNetworkFailureNoticeAt = now;
    logInfo(genericLogMessage, { error: message.error, networkFailure: true });
    showGenericErrorToast();
  }
  return true;
}

/**
 * Shows a toast for upload failure messages: prefer backend message id
 * when present, otherwise logs with genericCopy and shows generic failure.
 */
export function showFailUploadErrorToast(
  message: Message,
  genericLogMessage: string
): void {
  if (!message.error || noticeNetworkFailure(message, genericLogMessage)) {
    return;
  }

  const messageId =
    'messageId' in message && message.messageId
      ? String(message.messageId)
      : null;

  if (messageId) {
    const hideDelay = getErrorToastHideDelay(messageId);
    showErrorToastWithMessage(messageId, undefined, hideDelay);
  } else {
    showErrorToastAndLog(genericLogMessage, message.error);
  }
}
